'use client';

import {
  Box,
  Collapse,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { type ReactNode, useRef, useState } from 'react';
import { IconThumbDown, IconThumbUp } from '../Icons';
import useCookiebotConsent from '../hooks/useCookiebotConsent';
import { PrimaryButton } from './Button';
import TextField from './TextField';

export type ThumbsSurveyData = {
  thumbs: 'thumbs-up' | 'thumbs-down' | '';
  optionalText?: string;
  page_variant?: 'new' | 'old';
};

type ThumbsValue = ThumbsSurveyData['thumbs'];

export type ThumbsSurveyProps = {
  label?: ReactNode;
  onSubmit?: (data: ThumbsSurveyData) => void;
  slim?: boolean;
  thumbUpLabel?: string;
  thumbDownLabel?: string;
  feedbackPrompt?: ReactNode;
  thumbUpPrompt?: string;
  thumbDownPrompt?: string;
  submitLabel?: ReactNode;
  thankYouHeading?: ReactNode;
  thankYouText?: ReactNode;
  thankYouFooter?: ReactNode;
  respectCookiebotConsent?: boolean;
};

const thumbSize = 48;

const ThumbsSurvey = ({
  label,
  onSubmit,
  slim = false,
  feedbackPrompt = (
    <Typography variant='body2' color='textPrimary' sx={{ pb: 2 }}>
      Du kan påverka utvecklingen av sidan genom att skicka in konkreta
      synpunkter. Skriv inga personliga uppgifter för vi kan inte svara på ditt
      meddelande.
    </Typography>
  ),
  thumbUpPrompt = 'Vad är bra på sidan? (frivilligt)',
  thumbDownPrompt = 'Hur kan vi förbättra sidan? (frivilligt)',
  submitLabel = 'Skicka',
  thankYouHeading = 'Tack för dina synpunkter!',
  thankYouText = 'Vi går igenom och utvärderar alla synpunkter månadsvis.',
  thankYouFooter,
  thumbDownLabel,
  thumbUpLabel,
  respectCookiebotConsent = true,
}: ThumbsSurveyProps) => {
  const isVisible = useCookiebotConsent('statistics', respectCookiebotConsent);
  const [thumbs, setThumbs] = useState<ThumbsValue>('');
  const [optionalText, setOptionalText] = useState('');
  const [showPanelForm, setShowPanelForm] = useState(false);
  const theme = useTheme();
  const radioStyle = {
    border: '2px solid transparent',
    transition: 'border-color 0.2s ease-in-out, color 0.2s ease-in-out',
    '&.Mui-checked': {
      borderColor: theme.palette.primary.main,
    },
  };

  const inputSectionRef = useRef<HTMLDivElement>(null);

  const handleThumbChange = (value: ThumbsValue) => {
    setThumbs(value);
  };

  if (!isVisible) {
    return null;
  }

  if (showPanelForm) {
    return (
      <Box
        sx={{
          textAlign: 'left',
          mt: 4,
          maxWidth: '60ch',
          margin: '0 auto',
        }}
      >
        <Typography
          variant='h5'
          component='h2'
          sx={{ pt: 4, textAlign: 'left' }}
        >
          {thankYouHeading}
        </Typography>
        <Typography
          variant='body2'
          color='textSecondary'
          sx={{ pt: 2, textAlign: 'left' }}
        >
          {thankYouText}
        </Typography>
        {thankYouFooter ? <Box sx={{ pt: 2 }}>{thankYouFooter}</Box> : null}
      </Box>
    );
  }

  return (
    <Box
      component='form'
      onSubmit={(e) => {
        e.preventDefault();
        setShowPanelForm(true);
        onSubmit?.({ thumbs, optionalText });
      }}
    >
      <FormControl
        fullWidth
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          p: slim ? 0 : 4,
        }}
      >
        <FormLabel
          id='feedback-thumbs-label'
          sx={{
            fontSize: theme.typography.h5.fontSize,
            fontWeight: theme.typography.h5.fontWeight,
            lineHeight: theme.typography.h5.lineHeight,
            letterSpacing: theme.typography.h5.letterSpacing,
            textAlign: 'center',
          }}
        >
          {label}
        </FormLabel>
        <RadioGroup
          name='feedback-thumbs'
          value={thumbs}
          onChange={(e) =>
            handleThumbChange(
              e.target.value as 'thumbs-up' | 'thumbs-down' | '',
            )
          }
          aria-labelledby='feedback-thumbs-label'
          row
        >
          <FormControlLabel
            value={'thumbs-up' satisfies ThumbsSurveyData['thumbs']}
            labelPlacement='bottom'
            control={
              <Radio
                checkedIcon={<IconThumbUp sx={{ fontSize: thumbSize }} />}
                icon={<IconThumbUp sx={{ fontSize: thumbSize }} />}
                sx={radioStyle}
                // @ts-ignore data-tracking-id is not a valid prop in types, but is used by GTM
                slotProps={{ input: { 'data-tracking-id': 'thumbs-up' } }}
              />
            }
            label={
              <span data-tracking-id='thumbs-up'>{thumbUpLabel || 'Bra'}</span>
            }
          />
          <FormControlLabel
            value={'thumbs-down' satisfies ThumbsSurveyData['thumbs']}
            labelPlacement='bottom'
            control={
              <Radio
                checkedIcon={<IconThumbDown sx={{ fontSize: thumbSize }} />}
                icon={<IconThumbDown sx={{ fontSize: thumbSize }} />}
                sx={radioStyle}
                // @ts-ignore data-tracking-id is not a valid prop in types, but is used by GTM
                slotProps={{ input: { 'data-tracking-id': 'thumbs-down' } }}
              />
            }
            label={
              <span data-tracking-id='thumbs-down'>
                {thumbDownLabel || 'Dålig'}
              </span>
            }
          />
        </RadioGroup>
        <Collapse
          in={thumbs !== ''}
          sx={{ width: '100%', maxWidth: '60ch' }}
          onEntered={() => {
            if (inputSectionRef.current) {
              inputSectionRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
            }
          }}
        >
          <div ref={inputSectionRef}>
            {feedbackPrompt}
            <TextField
              value={optionalText}
              onChange={(e) => setOptionalText(e.target.value)}
              multiline
              rows={3}
              label={thumbs === 'thumbs-up' ? thumbUpPrompt : thumbDownPrompt}
              helperText={<>{optionalText.length}/100 tecken.</>}
              fullWidth
              slotProps={{ htmlInput: { maxLength: 100 } }}
            />
            <PrimaryButton type='submit' fullWidth>
              {submitLabel}
            </PrimaryButton>
          </div>
        </Collapse>
      </FormControl>
    </Box>
  );
};

export default ThumbsSurvey;
